import styles from "./SendCodeModal.module.scss";
import code from "../../../assets/pin-code.png";
import { useTranslation } from "react-i18next";
import close from "../../../assets/icons/close.svg";
import { useEffect, useRef, useState } from "react";
import Button from "../../button/Button";
import { useAppSelector } from "../../../store/hooks";
import {
  useVerifyPasswordConfirmationCodeMutation,
  useVerifyForgotPasswordMutation,
  useCancelSubscriptionMutation,
  usePasswordUpdateRequestMutation,
} from "../../../store/services/auth-api";
import { subscriptionData } from "../../../types/details-api-types";
import { trackEvent } from "../../../utils/analytics";
import { EVENT_NAMES } from "../../../types/event-names";

interface SendCodeModalProps {
  title: string;
  subtitle: string;
  message?: string;
  length?: number;
  handleCodeSubmit?: (code: string) => void;
  setIsModalSendLinkVisible?: (arg: boolean) => void;
  setIsModalNewPasswordVisible?: (arg: boolean) => void;
  setNextFlowModal?: (arg: boolean) => void;
  isForgotPasswordPage?: boolean;
  isCancellationConfirmation?: boolean;
  subscriptionItem?: subscriptionData | null;
  callback?: (action: string, arg?: string) => void;
  isCodeVerification?: boolean;
}

const SendCodeModal: React.FC<SendCodeModalProps> = ({
  title,
  subtitle,
  message,
  length = 6,
  handleCodeSubmit,
  setIsModalSendLinkVisible,
  setIsModalNewPasswordVisible,
  setNextFlowModal,
  isForgotPasswordPage,
  isCancellationConfirmation,
  subscriptionItem,
  callback,
  isCodeVerification = false
}) => {
  const authState = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const [values, setValues] = useState<string[]>(Array(length).fill(""));
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [errorMassage, setErrorMassage] = useState<String>("");
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [verifyPasswordConfirmationCode] = useVerifyPasswordConfirmationCodeMutation();
  const [verifyForgotPasswordMutation] = useVerifyForgotPasswordMutation();
  const [passwordUpdateRequest] = usePasswordUpdateRequestMutation();
  const [cancelSubscription] = useCancelSubscriptionMutation();
  const [resendCoolDown, setResendCoolDown] = useState<number>(0);

  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return; // Only allow numbers

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Move focus to the next input
    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    // If all fields are filled, call the onComplete callback
    if (newValues.every((val) => val !== "")) {
      onSendMagicCodeClickHadler(newValues);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !values[index] && index > 0) {
      // Move focus to the previous input if current is empty and Backspace is pressed
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData("Text").slice(0, length);
    if (!/^\d+$/.test(pastedData)) return; // Allow only numbers in paste

    const newValues = pastedData.split("").map((char, index) => (index < length ? char : ""));
    setValues(newValues);

    // Move focus to the last input
    inputsRef.current[Math.min(pastedData.length, length - 1)]?.focus();

    // If all fields are filled, call the onComplete callback
    if (newValues.length === length) {
      onSendMagicCodeClickHadler(newValues);
    }
  };

  const onSendMagicCodeClickHadler = async (newValues?: string[]) => {
    const magicCode = newValues?.length ? newValues : values;

    setIsLoader(true);
    try {
      if (isForgotPasswordPage) {
        trackEvent(EVENT_NAMES.FORGOT_PASSWORD_VERIFICATION_SENT);
        await verifyForgotPasswordMutation({ code: magicCode.join("") }).unwrap();
      } else {
        await verifyPasswordConfirmationCode({ code: magicCode.join("") }).unwrap();
      }

      if (isCancellationConfirmation) {
        setNextFlowModal!(true);
        setIsModalSendLinkVisible!(false);

        trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_EMAIL_SENT);
        await cancelSubscription({ subscription_id: subscriptionItem?.subscription_id })
          .unwrap()
          .then((success) => {
            setTimeout(() => {
              setIsLoader(false);
              callback!("success", success);
              trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_EMAIL_VERIFIED);
              trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_SUCCESS);
            }, 4500);
          })
          .catch((error) => {
            trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_EMAIL_FAILED);
            trackEvent(EVENT_NAMES.SUBSCRIPTION_CANCELLED_FAILED);

            callback!("error", error);
          });
      } else {
        setTimeout(() => {
          setIsModalSendLinkVisible!(false);
          setIsModalNewPasswordVisible!(true);
          setIsLoader(false);
        }, 3000);
      }
    } catch (e) {
      setIsLoader(false);
      setErrorMassage("Wrong Magic Code. type again.");
    }
  };

  const onClickResendEmailHandler = async () => {
    if (resendCoolDown > 0) return; // Prevent multiple clicks
    try {
      setResendCoolDown(30);
      await passwordUpdateRequest({ email: authState.user?.email, isCodeVerification: isCodeVerification }).unwrap();
    } catch (e) {
      if (e && typeof e === "object" && "data" in e && typeof e.data === "object" && "error" in e.data!) {
        setErrorMassage(`Error: ${(e as any).data.error}`);
      } else {
        // setErrorMassage("An unknown error occurred");
      }
      console.error(e);
    }
  };

  useEffect(() => {
    if (resendCoolDown === 0) return;

    const interval = setInterval(() => {
      setResendCoolDown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCoolDown]);

  useEffect(() => {
    onClickResendEmailHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.root}>
      {!isForgotPasswordPage && (
        <img src={close} alt="close" onClick={() => setIsModalSendLinkVisible!(false)} className={styles.root_close} />
      )}
      <img src={code} alt="code" />
      <p className={styles.root_title}>{title}</p>
      <div className={styles.root_info}>
        <p className={styles.root_subtitle}>{subtitle}</p>
        <p className={styles.root_message}>{message}</p>
      </div>
      <div className={styles.root_digits}>
        {values.map((value, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el!)}
            value={value}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            maxLength={1}
          />
        ))}
      </div>
      <div className={styles.root_footerSection}>
        <div className={styles.root_footer}>
          <Button
            onClick={onSendMagicCodeClickHadler}
            text={t("Confirm")}
            classNames={styles.root_footer_generate}
            isLoader={isLoader}
          />
          {errorMassage && <p className={styles.root_footer_error}>{t(errorMassage as string)}</p>}
        </div>

        <div className={styles.root_footerSection_resendEmail}>
          <span
            className={`${styles.root_reSend} ${resendCoolDown > 0 ? styles.root_reSend_disabled : ""}`}
            onClick={onClickResendEmailHandler}
          >
            {resendCoolDown > 0 ? `${t("Re-send Email")}: ${resendCoolDown}` : t("Re-send Email")}
          </span>
          <div className={styles.root_footerSection_resendEmail_support}>
            <p>{t("Need Help?")}</p>
            <a href="https://www.fortect.com/support/" className={styles.root_footer_link}>
              {" "}
              {t("Contact support")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendCodeModal;
